import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './JqCommissionGuide.css';
import { money } from '../utils/money';

const API_BASE = '/api';
const rm = (n) => money(Number(n) || 0);
const monthLabel = (m) => new Date(m + '-01').toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });

// Dedicated invoice-breakdown page for standard-tiered salespeople. Shows the
// 'ready' invoices behind a single month's eligible revenue. The month is driven
// by the top-bar month·year picker (passed in as the `month` prop), so changing
// the month up top re-fetches and swaps this page to that month.
function SalesBreakdown({ month }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/commission-guide/${id}/invoices?month=${month}`, { headers: headers() });
      setData(res.data);
    } catch (e) {
      setData(null);
      setError(e.response?.data?.error || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [id, month]);

  useEffect(() => { load(); }, [load]);

  const name = data?.salesperson?.name || '';
  const invoices = data?.invoices || [];

  return (
    <div className="jqc-page">
      <div className="jqc-head">
        <h1>Sales Breakdown{name ? ` — ${name}` : ''}</h1>
        <span className="jqc-muted">
          {monthLabel(month)}
          {data && data.count != null ? ` · ${data.count} invoice${data.count === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      {error && <div className="jqc-error">{error}</div>}

      <div className="jqc-section-title">Invoice Breakdown ({monthLabel(month)})</div>
      <div className="report-table-scroll" role="region" aria-label="Report table — scroll to view all columns" tabIndex={0}><table className="jqc-table">
        <thead><tr><th>Date</th><th>Invoice #</th><th>Customer</th><th>Venue</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
        <tbody>
          {loading && <tr><td colSpan={5} className="jqc-muted">Loading…</td></tr>}
          {!loading && !error && invoices.map((iv, idx) => (
            <tr key={idx}>
              <td style={{ whiteSpace: 'nowrap' }}>{iv.date}</td>
              <td>{iv.invoiceNumber}</td>
              <td>{iv.customer || '—'}</td>
              <td>{iv.venue || '—'}</td>
              <td style={{ textAlign: 'right' }}>{rm(iv.amount)}</td>
            </tr>
          ))}
          {!loading && !error && invoices.length === 0 && (
            <tr><td colSpan={5} className="jqc-muted">No invoices for this month.</td></tr>
          )}
          {!loading && !error && invoices.length > 0 && (
            <tr className="total">
              <td colSpan={4}>Total (eligible revenue)</td>
              <td style={{ textAlign: 'right' }}>{rm(data.total || 0)}</td>
            </tr>
          )}
        </tbody>
      </table></div>
      <div className="jqc-note">These are the "ready" invoices that make up this month's eligible revenue. Change the month in the top bar to view another month.</div>
    </div>
  );
}

export default SalesBreakdown;
